import { deleteApi, getApi } from 'apis';
import { PermissionContext } from 'components/Global/AuthLayout';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import TableActionHeader from 'components/Global/TableActionHeader';
import { SideDrawerWrapper } from 'components/Global';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConstantDropdownAction } from '../../../redux/actions/constantDropdownAction';
import { AppDispatch } from 'redux/store';
import { toastText } from 'utils/utils';
import AddCoaBody from './AddCoaBody';
import CoaTable from './Table';
import { CoaDetailResponse, CoaListResponse, CoaRecord } from './types';
import styles from './index.module.scss';

interface DepartmentDropdownOption {
	label: string;
	value: string;
	branchId: string;
}

const CoaTables: React.FC = () => {
	const permissions = useContext(PermissionContext);
	const dispatch = useDispatch<AppDispatch>();
	const dropdownOptions = useSelector(
		(state: { constantDropdown: { data: Record<string, unknown> | null } }) =>
			state.constantDropdown.data,
	);
	const { isLoading: isDropdownLoading } = useSelector(
		(state: { constantDropdown: { isLoading: boolean } }) =>
			state.constantDropdown,
	);

	const [coaData, setCoaData] = useState<CoaRecord[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [totalRecords, setTotalRecords] = useState(0);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [searchValue, setSearchValue] = useState('');
	const [typeFilter, setTypeFilter] = useState<string | null>(null);
	const [branchFilter, setBranchFilter] = useState<string[]>([]);
	const [departmentFilter, setDepartmentFilter] = useState<string[]>([]);
	const [drawerAnimation, setDrawerAnimation] = useState(false);
	const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editSelected, setEditSelected] = useState<CoaRecord | null>(null);
	const [deleteId, setDeleteId] = useState<string | null>(null);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [drawerTitle, setDrawerTitle] = useState('Add Chart of Accounts');

	const debouncedSearch = useDebounce(searchValue, 500);

	const branchOptions = useMemo(
		() =>
			(
				(dropdownOptions?.branchCode as { label: string; value: string }[]) ??
				[]
			).filter((option) => option.value),
		[dropdownOptions?.branchCode],
	);

	const allDepartments = useMemo(
		() =>
			(
				(dropdownOptions?.department as DepartmentDropdownOption[]) ?? []
			).filter((option) => option.value),
		[dropdownOptions?.department],
	);

	const departmentOptions = useMemo(() => {
		if (!branchFilter.length) {
			return allDepartments;
		}

		return allDepartments.filter((dept) =>
			branchFilter.includes(dept.branchId),
		);
	}, [allDepartments, branchFilter]);

	const departmentLabelById = useMemo(() => {
		const map = new Map<string, string>();
		allDepartments.forEach((dept) => map.set(dept.value, dept.label));
		return map;
	}, [allDepartments]);

	const branchLabelById = useMemo(() => {
		const map = new Map<string, string>();
		branchOptions.forEach((branch) => map.set(branch.value, branch.label));
		return map;
	}, [branchOptions]);

	useEffect(() => {
		dispatch(fetchConstantDropdownAction({}));
	}, [dispatch]);

	const fetchCoaData = async () => {
		setIsLoading(true);
		try {
			const query: Record<string, string | number> = { limit, page };

			if (debouncedSearch.trim()) {
				query.search = debouncedSearch.trim();
			}
			if (typeFilter) {
				query.type = typeFilter;
			}
			if (branchFilter.length) {
				query.branchIds = branchFilter.join(',');
			}
			if (departmentFilter.length) {
				query.departmentIds = departmentFilter.join(',');
			}

			const response = await getApi('/coa/getAllCoa', query);
			const { data, total } = response.data as CoaListResponse;
			setCoaData(data);
			setTotalRecords(total);
		} catch (error: any) {
			toastText(
				error?.response?.data?.message || 'Failed to fetch COA data',
				'error',
			);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchCoaData();
	}, [
		page,
		limit,
		debouncedSearch,
		typeFilter,
		branchFilter,
		departmentFilter,
	]);

	const handleSearch = (value: string) => {
		if (value !== searchValue) {
			setPage(1);
		}
		setSearchValue(value);
	};

	const handleSearchApi = (_value: string) => {};

	const handleTypeChange = (value: string | null) => {
		setTypeFilter(value);
		setPage(1);
	};

	const handleBranchChange = (value: string | number | string[]) => {
		const branchIds = Array.isArray(value) ? value.map(String) : [];
		setBranchFilter(branchIds);

		setDepartmentFilter((prev) => {
			if (!branchIds.length) {
				return [];
			}

			return prev.filter((id) => {
				const dept = allDepartments.find((item) => item.value === id);
				return dept ? branchIds.includes(dept.branchId) : false;
			});
		});
		setPage(1);
	};

	const handleDepartmentChange = (value: string | number | string[]) => {
		setDepartmentFilter(Array.isArray(value) ? value.map(String) : []);
		setPage(1);
	};

	const paginationChangeHandler = (pageNo: number, pageSize: number) => {
		setPage(pageNo);
		setLimit(pageSize);
	};

	const openAddDrawer = () => {
		setEditSelected(null);
		setDrawerTitle('Add Chart of Accounts');
		setDrawerAnimation(true);
		setIsSideDrawerOpen(true);
	};

	const openEditDrawer = async (record: CoaRecord) => {
		try {
			const res = await getApi(`/coa/${record.id}`);
			const { data } = res.data as CoaDetailResponse;
			setEditSelected(data);
			setDrawerTitle('Edit Chart of Accounts');
			setDrawerAnimation(true);
			setIsSideDrawerOpen(true);
		} catch (err: any) {
			toastText(
				err?.response?.data?.message || 'Failed to load COA account',
				'error',
			);
		}
	};

	const closeDrawerByAnimation = () => {
		setDrawerAnimation(false);
	};

	const removeDrawerFromDom = () => {
		setIsSideDrawerOpen(false);
		setEditSelected(null);
	};

	const confirmDelete = (record: CoaRecord) => {
		setDeleteId(record.id);
		setIsModalOpen(true);
	};

	const handleDelete = async () => {
		if (!deleteId) return;
		try {
			setIsDeleteLoading(true);
			const res = await deleteApi(`/coa/${deleteId}`);
			toastText(res.data.message || 'COA deleted successfully', 'success');
			setIsModalOpen(false);
			setDeleteId(null);
			if (totalRecords > 1 && totalRecords % limit === 1 && page > 1) {
				setPage(page - 1);
			} else {
				fetchCoaData();
			}
		} catch (err: any) {
			toastText(
				err?.response?.data?.message || 'Failed to delete COA account',
				'error',
			);
		} finally {
			setIsDeleteLoading(false);
		}
	};

	return (
		<>
			<div className={styles['coa-table']}>
				<TableActionHeader title={'Chart of Accounts'}>
					<div className={styles['coa-table__action']}>
						{permissions.isAdmin && (
							<button
								className={`btn-blue ${styles['coa-table__action--button']}`}
								onClick={openAddDrawer}
								disabled={isLoading}
							>
								<p>Add Account</p>
							</button>
						)}
					</div>
				</TableActionHeader>
				<CoaTable
					data={coaData}
					loading={isLoading}
					paginationChangeHandler={paginationChangeHandler}
					totalRecords={totalRecords}
					currentPage={page}
					onEdit={openEditDrawer}
					onDelete={confirmDelete}
					branchLabelById={branchLabelById}
					departmentLabelById={departmentLabelById}
					searchValue={searchValue}
					handleSearch={handleSearch}
					handleSearchApi={handleSearchApi}
					typeFilter={typeFilter}
					onTypeChange={handleTypeChange}
					branchFilter={branchFilter}
					onBranchChange={handleBranchChange}
					branchOptions={branchOptions}
					departmentFilter={departmentFilter}
					onDepartmentChange={handleDepartmentChange}
					departmentOptions={departmentOptions}
					isDropdownLoading={isDropdownLoading}
				/>
			</div>
			{isSideDrawerOpen && (
				<SideDrawerWrapper
					isOpen={drawerAnimation}
					removeDrawerFromDom={removeDrawerFromDom}
					closeDrawerByAnimation={closeDrawerByAnimation}
					headerTitle={drawerTitle}
					position="right"
					width="half"
				>
					<AddCoaBody
						closeDrawerByAnimation={closeDrawerByAnimation}
						fetchCoa={fetchCoaData}
						editSelected={editSelected}
					/>
				</SideDrawerWrapper>
			)}
			<ConfirmDelete
				isModalOpen={isModalOpen}
				handleOk={handleDelete}
				handleCancel={() => {
					setIsModalOpen(false);
					setDeleteId(null);
				}}
				deleteHandler={handleDelete}
				isLoading={isDeleteLoading}
				deleteMessage="Are you sure you want to delete this chart of accounts entry?"
			/>
		</>
	);
};

export default CoaTables;
